import React from 'react';
import { Avatar, Box, CircularProgress, Dialog } from '@mui/material';
import { Check, Info, Mail, Plus, X } from 'lucide-react';

import Text from 'components/text';
import { CcMatchPreview } from 'hooks/swrRequests/useAdminCcRequests';
import type { Artist } from 'types';

import { FONT } from './constants';
import { getInitials } from './get-initials';
import MatchCard, { matchMetaLine } from './components/match-card';
import PrimaryButton from './components/primary-button';
import SecondaryButton from 'components/admin/secondary-button';

interface ConfirmLogRequestModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
  // null → the request creates no match; a non-empty array → one entry per matched pair.
  preview: CcMatchPreview[] | null;
  fromArtist: Artist | null;
  wants: Artist[];
}

// Used by the no-match summary: avatar + name + meta + optional right slot.
const ArtistRow: React.FC<{ artist: Artist; right?: React.ReactNode }> = ({ artist, right }) => (
  <Box display="flex" alignItems="center" gap={1.5} padding="10px 0">
    <Avatar src={artist.image ?? undefined} sx={{ width: 36, height: 36, fontSize: 12 }}>
      {getInitials(artist.name)}
    </Avatar>
    <Box flex={1} minWidth={0}>
      <Text fontSize={FONT.base} fontWeight={600}>
        {artist.name}
      </Text>
      {matchMetaLine(artist) && (
        <Text fontSize={FONT.sm} color="#64748b">
          {matchMetaLine(artist)}
        </Text>
      )}
    </Box>
    {right}
  </Box>
);

const Banner: React.FC<{
  tone: 'success' | 'muted';
  icon: React.ReactNode;
  title: string;
  sub: string;
}> = ({ tone, icon, title, sub }) => {
  const c =
    tone === 'success' ? { bg: '#dcfce7', color: '#15803d' } : { bg: '#f1f2f4', color: '#64748b' };
  return (
    <Box
      display="flex"
      gap={1.25}
      alignItems="flex-start"
      padding="12px 14px"
      borderRadius="8px"
      bgcolor={c.bg}
      marginBottom="18px"
    >
      <Box component="span" display="flex" color={c.color} sx={{ flexShrink: 0, marginTop: '1px' }}>
        {icon}
      </Box>
      <Box minWidth={0}>
        <Text fontSize={FONT.base} fontWeight={700} color={c.color}>
          {title}
        </Text>
        <Text fontSize={FONT.sm} color="#64748b" marginTop="2px" sx={{ whiteSpace: 'normal' }}>
          {sub}
        </Text>
      </Box>
    </Box>
  );
};

const ConfirmLogRequestModal: React.FC<ConfirmLogRequestModalProps> = ({
  open,
  onClose,
  onConfirm,
  submitting,
  preview,
  fromArtist,
  wants,
}) => {
  // "No match" comes back as null (or an empty body → ""), so guard for a real array.
  const matches = Array.isArray(preview) ? preview : [];
  const total = matches.length;
  const isMatch = total > 0;
  const plural = total > 1;
  // Only artists with an account on file get an intro email (the backend skips the rest),
  // so count the real recipients rather than assuming 2 per pair.
  const emailCount = matches.reduce(
    (n, m) => n + (m.artist1User?.email ? 1 : 0) + (m.artist2User?.email ? 1 : 0),
    0
  );
  const emailWord = emailCount === 1 ? 'email' : 'emails';
  const wantsNames =
    wants
      .map((w) => w.name)
      .filter(Boolean)
      .join(', ') || '—';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', '& .MuiAvatar-root': { fontFamily: 'inherit' } } }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        padding="22px 24px 16px"
      >
        <Box minWidth={0}>
          <Text fontSize={FONT.lg} fontWeight={700}>
            {isMatch
              ? plural
                ? 'Confirm connections'
                : 'Confirm connection'
              : 'Log this request?'}
          </Text>
        </Box>
        <Box
          component="button"
          onClick={onClose}
          aria-label="Close"
          display="flex"
          sx={{
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            padding: 0,
            color: '#64748b',
          }}
        >
          <X size={20} />
        </Box>
      </Box>

      {/* Body */}
      <Box padding="0 24px 8px" sx={{ maxHeight: '64vh', overflowY: 'auto' }}>
        {isMatch ? (
          <>
            <Banner
              tone="success"
              icon={<Check size={16} />}
              title={`This creates ${total} match${plural ? 'es' : ''}`}
              sub={`${total} pairing${plural ? 's' : ''} · ${emailCount} ${emailWord} to send`}
            />

            {matches.map((match, i) => (
              <MatchCard
                key={`${match.artist1.id}-${match.artist2.id}`}
                match={match}
                index={i}
                total={total}
              />
            ))}
          </>
        ) : (
          <>
            <Banner
              tone="muted"
              icon={<Info size={16} />}
              title="No match yet"
              sub="No emails are sent."
            />

            <Box border="1px solid #e2e8f0" borderRadius="8px" padding="2px 14px 12px">
              {fromArtist && <ArtistRow artist={fromArtist} />}
              <Text fontSize={FONT.sm} color="#64748b" sx={{ whiteSpace: 'normal' }}>
                <Box component="span" color="#0f172a" fontWeight={600}>
                  Wants to collab with:
                </Box>{' '}
                {wantsNames}
              </Text>
            </Box>
          </>
        )}
      </Box>

      {/* Footer */}
      <Box
        display="flex"
        justifyContent="flex-end"
        gap={1}
        padding="16px 24px"
        borderTop="1px solid #e2e8f0"
        marginTop="12px"
      >
        <SecondaryButton variant="text" onClick={onClose} disabled={submitting}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          variant="contained"
          onClick={onConfirm}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : isMatch && emailCount > 0 ? (
              <Mail size={16} />
            ) : isMatch ? (
              <Check size={16} />
            ) : (
              <Plus size={16} />
            )
          }
        >
          {!isMatch
            ? 'Log request'
            : emailCount > 0
            ? `Confirm & send ${emailCount} ${emailWord}`
            : 'Confirm connection'}
        </PrimaryButton>
      </Box>
    </Dialog>
  );
};

export default ConfirmLogRequestModal;
